import React, { ChangeEvent, useEffect, useState } from 'react';
import { ContextMenuAction } from 'src/components/common/ContextMenu';
import Owner from 'src/models/tract_detail/Owner';
import AddressUtil from 'src/util/Address';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import Ownership from 'src/models/tract_detail/Ownership';
import { Url } from 'src/api';
import { useFetch } from 'use-http';
import { useSelector } from 'react-redux';
import { IRootState } from 'src/reducers';
import OwnershipContactCategory from 'src/models/OwnershipContactCategory';
import { UseFormMethods } from 'react-hook-form';
import Card from 'src/components/common/Card.redesign';
import AnimateHeight from 'react-animate-height';
import AdditionOwnershipType from 'src/models/tract_detail/AdditionalOwnershipType';
import { SelectItem } from 'src/models/common/SelectItem';
import MathUtil from 'src/util/MathUtil';

interface IGlobalOwnershipCardStateProps extends Partial<Pick<UseFormMethods, 'register' | 'errors' | 'reset'>> {
    index: number;
    namePrefix: string;
    contactCategories: Array<OwnershipContactCategory>;
    ownership: Ownership;
    owners: Array<Owner>;
    grossAcres: number;
    projectType?: 'Mineral' | 'Surface';
    errorMessage?: string;
    ownershipActions: ContextMenuAction[];
    isChild?: boolean;
    leaseBuyerList: Array<SelectItem>;
    hasNEMI: boolean;
    updateOwnershipInterest: () => void;
    updateOwnershipNote: (ownership: Ownership) => void;
    openOwnershipNotes?: (ownership: Ownership) => void;
    createAdditionalOwnership?: (ownershipId: number) => void;
    deleteOwnership?: (ownershipId: number) => void;
}

const GlobalOwnershipCard = (props: IGlobalOwnershipCardStateProps) => {
    const { get, response } = useFetch();
    const appContext = useSelector((state: IRootState) => state.appContext);

    const [owner, setOwner] = useState<Owner>(undefined);
    const [netAcres, setNetAcres] = useState<number>(0);
    const [contactCategoryOptions, setContactCategoryOptions] = useState<{ value: string | number; label: string }[]>([]);
    const [leaseBuyerOptions, setLeaseBuyerOptions] = useState<{ value: string | number; label: string }[]>([]);
    const [additionalHeight, setAdditionalHeight] = useState<string | number>(0);

    useEffect(() => {
        if (props.ownership?.ownerLookUpId) {
            loadOwner(props.ownership.ownerLookUpId);
            let interest = props.ownership.decimalInterest;
            let net = interest * props.grossAcres;
            setNetAcres(!isNaN(net) ? parseFloat(MathUtil.cleanNumber(net)) : 0);
        }
    }, [props.grossAcres, props.ownership?.ownerLookUpId]);

    // Prevents undefined mapping error for RHF Select
    useEffect(() => {
        if (props.contactCategories?.length > 0) {
            let options = props.contactCategories.map((cc) => {
                return { value: cc.id, label: cc.name };
            });
            setContactCategoryOptions(options);
        }
    }, [props.contactCategories]);

    useEffect(() => {
        if (props.leaseBuyerList?.length > 0) {
            let options = props.leaseBuyerList.map((cc) => {
                return { value: cc.id, label: cc.name };
            });
            setLeaseBuyerOptions(options);
        }
    }, [props.leaseBuyerList]);

    useEffect(() => {
        props.reset();
    }, [contactCategoryOptions]);

    // TODO: Move this up to a list of owners, so that the endpoint isn't getting called for every owner.
    // Note: Existing owners already exist. Might just be able to move that list and use a filter.
    const loadOwner = async (ownerId: number) => {
        const result = await get(`${Url.project.getProject(appContext, appContext.projectNumber)}/owners/${ownerId}`);

        if (response.ok) {
            if (result) {
                result.addressLine1 = result?.address?.addressLine1;
                result.city = result?.address?.city;
                result.state = result?.address?.state?.abbreviation;
            }
            setOwner(result);
        }
    };

    const calculateAcreage = (e: ChangeEvent<HTMLInputElement>) => {
        console.log('Update Acreage');
        let net = 0;
        let refInterest = e.currentTarget.value;
        net = parseFloat(refInterest) * props?.grossAcres;
        setNetAcres(!isNaN(net) ? parseFloat(MathUtil.cleanNumber(net)) : 0);
        props.updateOwnershipInterest();
    };

    // TODO: Establish action Item functionality for Additional Ownerships
    const makeMineralOwnership = (ownershipId: number) => {
        console.log('Make Mineral Ownership');
    };

    // const navigateLeaseInfo = (ownershipId: number) => {
    //     console.log('Navigate Lease Info');
    // };

    const toggleAdditionalOwnerships = (e: unknown) => {
        const newHeight = additionalHeight === 'auto' ? 0 : 'auto';
        setAdditionalHeight(newHeight);
    };

    const additionalOwnership = () => {
        return props.ownership.additionalOwnerships.map((ao, index) => {
            const additionalOwnershipActions: ContextMenuAction[] = [
                { text: 'Delete ownership', action: () => props.deleteOwnership(ao.id) },
                { text: 'Ownership notes', action: () => props.openOwnershipNotes(ao) },
            ];

            if (
                ao.additionalOwnershipTypeId !== AdditionOwnershipType.NonExecutive &&
                ao.additionalOwnershipTypeId !== AdditionOwnershipType.NonParticipating
            ) {
                additionalOwnershipActions.push({ text: 'Make mineral owner', action: () => makeMineralOwnership(ao.id) });
            }

            return (
                <GlobalOwnershipCard
                    key={`additionalOwnership[${index}]`}
                    index={index}
                    namePrefix={`${props.namePrefix}[${props.index}].additionalOwnerships`}
                    contactCategories={props.contactCategories}
                    ownership={ao}
                    owners={props.owners}
                    grossAcres={props.grossAcres}
                    projectType={props.projectType}
                    leaseBuyerList={props.leaseBuyerList}
                    register={props.register}
                    errors={props.errors}
                    errorMessage={''}
                    isChild={true}
                    hasNEMI={props.hasNEMI}
                    reset={() => props.reset()}
                    ownershipActions={additionalOwnershipActions}
                    updateOwnershipInterest={props.updateOwnershipInterest}
                    updateOwnershipNote={(ownership: Ownership) => props.updateOwnershipNote(ownership)}
                    createAdditionalOwnership={(ownershipId: number) => props.createAdditionalOwnership(ownershipId)}
                    deleteOwnership={props.deleteOwnership}
                />
            );
        });
    };

    // TODO: Fix these so they're not hard-coded values.
    const determineOwnerType = (id: number) => {
        let type = 'Unknown';
        switch (id) {
            case 1:
                type = 'Heir';
                break;
            case 2:
                type = 'Remainderman';
                break;
            case 3:
                type = 'Non-Executive';
                break;
            case 4:
                type = 'Non-Participating';
                break;
            case 5:
                type = 'Transfer On Death';
                break;
            case 6:
                type = 'Naked Owner';
                break;
            default:
                break;
        }

        return type;
    };

    const ownerDescription = () => {
        let fullName = owner.name + (owner?.suffix?.length > 0 ? (' ' + owner?.suffix) : '');
        if (owner.spouseName?.length > 1) {
            fullName += ' & ' + owner.spouseName;
        }
        if (props.isChild === true) {
            return `${fullName} - ${determineOwnerType(props.ownership.additionalOwnershipTypeId)}`;
        }
        return fullName;
    };

    return (
        <Card
            badge={`${props.ownership.id}`}
            contextActions={props.ownershipActions}
            newContext={true}
            showMenu={true}
            className="small-input"
            infoBar={owner?.isClientAcreage || props?.ownership?.isWIClientAcreage ? 'Is Client leased / owned Acreage' : undefined}
        >
            <div
                key={props.ownership.id}
                style={{ minHeight: '5rem', margin: '1.3rem 1rem 0 1rem', display: 'flex', justifyContent: 'space-between' }}
            >
                <div style={{ paddingLeft: '2.5rem', fontSize: '1.6rem' }}>
                    {owner && (
                        <>
                            {ownerDescription()}
                            <div style={{ fontSize: '1.4rem' }}>{AddressUtil.concatWithCommas(owner.addressLine1, owner.city, owner.state)}</div>
                        </>
                    )}
                </div>

                {/* Hidden Values */}
                <div>
                    <div className="mr-1" hidden={true}>
                        <RHFInputBox
                            label=""
                            name={`${props.namePrefix}[${props.index}].id`}
                            register={props.register}
                            disabled={true}
                            errors={props.errors}
                        />
                    </div>
                    <div className="mr-1" hidden={true}>
                        <RHFInputBox
                            label=""
                            name={`${props.namePrefix}[${props.index}].parentOwnershipId`}
                            register={props.register}
                            disabled={true}
                            errors={props.errors}
                        />
                    </div>
                    <div className="mr-1" hidden={true}>
                        <RHFInputBox
                            label=""
                            name={`${props.namePrefix}[${props.index}].additionalOwnershipTypeId`}
                            register={props.register}
                            disabled={true}
                            errors={props.errors}
                        />
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div className="mr-1">
                        <RHFSelect
                            label="Contact Category"
                            name={`${props.namePrefix}[${props.index}].ownershipContactCategoryId`}
                            options={contactCategoryOptions}
                            register={props.register}
                            errors={props.errors}
                        />
                    </div>
                    <div className="mr-1">
                        <RHFSelect
                            label="Lease Buyer"
                            name={`${props.namePrefix}[${props.index}].leaseBuyer`}
                            options={leaseBuyerOptions}
                            register={props.register}
                            errors={props.errors}
                        />
                    </div>
                    {(props.hasNEMI === true && props.ownership.additionalOwnershipTypeId !== AdditionOwnershipType.NonExecutive) && (
                        <div className="mr-1">
                            <RHFInputBox
                                label="Executive Rights"
                                name={`${props.namePrefix}[${props.index}].executiveRights`}
                                onChange={props.updateOwnershipInterest}
                                type="number"
                                register={props.register}
                                errors={props.errors}
                            />
                        </div>
                    )}
                    <div className="mr-1">
                        <RHFInputBox
                            label="Interest"
                            name={`${props.namePrefix}[${props.index}].decimalInterest`}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => calculateAcreage(e)}
                            type="number"
                            register={props.register}
                            errors={props.errors}
                            step={0.0001}
                            rules={{ min: 0 }}
                        />
                    </div>
                    {!props.isChild && (
                        <div>
                            Net Acres
                            <div style={{ fontSize: '1.4rem' }}>{netAcres}</div>
                        </div>
                    )}
                </div>
            </div>

            {props.ownership?.additionalOwnerships?.length > 0 && (
                <>
                    <div onClick={toggleAdditionalOwnerships} style={{ marginBottom: '1rem', marginLeft: '1rem' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '1.75rem' }}>{additionalHeight === 'auto' ? '-' : '+'}</span>
                        {`Additional Ownerships (${props.ownership.additionalOwnerships.length})`}
                    </div>
                    <AnimateHeight duration={300} height={additionalHeight}>
                        {additionalOwnership()}
                    </AnimateHeight>
                </>
            )}
        </Card>
    );
};

export default GlobalOwnershipCard;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/OwnershipCard.redesign.tsx